<div class="dsg-layers">
  <p class="dsg-layers-empty" *ngIf="isEmpty()">
    Nothing on this page yet — drag a component from the palette below.
  </p>

  <ng-container *ngFor="let row of rows(); trackBy: trackByRow">
    <!-- ── Layer header ──────────────────────────────────────────────────── -->
    <div
      *ngIf="row.type === 'layer'"
      class="dsg-layer-row dsg-layer-band"
      [class.is-active]="activeLayerId() === row.id"
      [class.is-hidden]="!row.visible"
      [class.is-locked]="row.locked"
      [attr.data-drop]="dropHintFor(row)"
      [style.padding-left.px]="8 + row.depth * 14"
      draggable="true"
      (click)="onRowClick(row, $event)"
      (dragstart)="onDragStart(row, $event)"
      (dragover)="onDragOver(row, $event)"
      (dragleave)="onDragLeave(row)"
      (drop)="onDrop(row, $event)"
      (dragend)="onDragEnd()"
    >
      <button
        type="button"
        class="dsg-layer-expand"
        (click)="toggleExpanded(row.id, $event)"
        [attr.aria-label]="row.expanded ? 'Collapse' : 'Expand'"
      >
        <mat-icon>{{ row.expanded ? 'expand_more' : 'chevron_right' }}</mat-icon>
      </button>

      <mat-icon class="dsg-layer-icon" aria-hidden="true">{{ layerIconFor(row.layer) }}</mat-icon>

      <input
        *ngIf="editingId() === row.id; else bandName"
        class="dsg-layer-rename"
        type="text"
        [value]="row.layer.name"
        (click)="$event.stopPropagation()"
        (blur)="commitRename(row.id, $any($event.target).value)"
        (keydown.enter)="$any($event.target).blur()"
        (keydown.escape)="editingId.set(null)"
        autofocus
      />
      <ng-template #bandName>
        <span
          class="dsg-layer-name"
          [matTooltip]="kindLabels[row.layer.kind] + ' layer'"
          (dblclick)="startRename(row.id, $event)"
        >{{ row.layer.name }}</span>
        <span class="dsg-layer-count">{{ row.count }}</span>
      </ng-template>

      <div class="dsg-layer-actions">
        <button
          type="button"
          class="dsg-layer-action"
          [class.is-on]="!row.visible"
          (click)="onLayerFlag(row, 'visible', $event)"
          [matTooltip]="row.visible ? 'Hide layer' : 'Show layer'"
        >
          <mat-icon>{{ row.visible ? 'visibility' : 'visibility_off' }}</mat-icon>
        </button>
        <button
          type="button"
          class="dsg-layer-action"
          [class.is-on]="row.locked"
          (click)="onLayerFlag(row, 'locked', $event)"
          [matTooltip]="row.locked ? 'Unlock layer' : 'Lock layer'"
        >
          <mat-icon>{{ row.locked ? 'lock' : 'lock_open' }}</mat-icon>
        </button>
        <!-- Only an empty layer can go: deleting content is the node's own job. -->
        <button
          *ngIf="!row.count"
          type="button"
          class="dsg-layer-action dsg-layer-delete"
          (click)="deleteLayer.emit(row.id); $event.stopPropagation()"
          matTooltip="Delete layer"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>

    <!-- ── Node ──────────────────────────────────────────────────────────── -->
    <div
      *ngIf="row.type === 'node'"
      class="dsg-layer-row"
      [class.is-selected]="isSelected(row.node.id)"
      [class.is-hidden]="!row.node.visible"
      [class.is-locked]="row.node.locked"
      [class.is-reference]="row.reference"
      [attr.data-drop]="dropHintFor(row)"
      [style.padding-left.px]="8 + row.depth * 14"
      [attr.draggable]="row.reference ? null : true"
      (click)="onRowClick(row, $event)"
      (dragstart)="onDragStart(row, $event)"
      (dragover)="onDragOver(row, $event)"
      (dragleave)="onDragLeave(row)"
      (drop)="onDrop(row, $event)"
      (dragend)="onDragEnd()"
    >
      <!-- Expand / collapse. Non-containers get a spacer so names stay aligned. -->
      <button
        *ngIf="row.hasChildren; else expandSpacer"
        type="button"
        class="dsg-layer-expand"
        (click)="toggleExpanded(row.id, $event)"
        [attr.aria-label]="row.expanded ? 'Collapse' : 'Expand'"
      >
        <mat-icon>{{ row.expanded ? 'expand_more' : 'chevron_right' }}</mat-icon>
      </button>
      <ng-template #expandSpacer><span class="dsg-layer-expand-spacer"></span></ng-template>

      <mat-icon class="dsg-layer-icon" aria-hidden="true">{{ iconFor(row.node) }}</mat-icon>

      <input
        *ngIf="editingId() === row.id; else layerName"
        class="dsg-layer-rename"
        type="text"
        [value]="row.node.name"
        (click)="$event.stopPropagation()"
        (blur)="commitRename(row.id, $any($event.target).value)"
        (keydown.enter)="$any($event.target).blur()"
        (keydown.escape)="editingId.set(null)"
        autofocus
      />
      <ng-template #layerName>
        <span
          class="dsg-layer-name"
          [title]="row.node.name"
          (dblclick)="startRename(row.id, $event)"
        >{{ row.node.name }}</span>
      </ng-template>

      <div class="dsg-layer-actions" *ngIf="!row.reference">
        <button
          type="button"
          class="dsg-layer-action"
          [class.is-on]="!row.node.visible"
          (click)="toggleVisible.emit(row.node.id); $event.stopPropagation()"
          [matTooltip]="row.node.visible ? 'Hide' : 'Show'"
        >
          <mat-icon>{{ row.node.visible ? 'visibility' : 'visibility_off' }}</mat-icon>
        </button>
        <button
          type="button"
          class="dsg-layer-action"
          [class.is-on]="row.node.locked"
          (click)="toggleLocked.emit(row.node.id); $event.stopPropagation()"
          [matTooltip]="row.node.locked ? 'Unlock' : 'Lock'"
        >
          <mat-icon>{{ row.node.locked ? 'lock' : 'lock_open' }}</mat-icon>
        </button>
        <button
          type="button"
          class="dsg-layer-action dsg-layer-delete"
          (click)="deleteNode.emit(row.node.id); $event.stopPropagation()"
          matTooltip="Delete"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>
  </ng-container>

  <!-- A band for something the auto-filing has no kind for — a background
       image, an overlay panel — which is then the drop target for new nodes. -->
  <div class="dsg-layers-add" *ngIf="screenId() && addableKinds().length">
    <button
      type="button"
      *ngFor="let kind of addableKinds()"
      class="dsg-layers-add-btn"
      (click)="onAddLayer(kind)"
      [matTooltip]="'Add a ' + kindLabels[kind] + ' layer'"
    >+ {{ kindLabels[kind] }}</button>
  </div>
</div>
